import { useState, useEffect, useCallback } from 'react';
import { searchApiService, SearchResult } from '../services/searchApi';

export const useTickerSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);

  const searchTickers = useCallback(async (term: string) => {
    if (term.length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await searchApiService.searchTickers(term);
      setResults(response.data.results || []);
      setShowResults(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
      setResults([]);
      setShowResults(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm) {
        searchTickers(searchTerm);
      } else {
        setResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchTickers]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const selectTicker = (ticker: SearchResult) => {
    setSearchTerm(ticker.ticker);
    setShowResults(false);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setResults([]);
    setShowResults(false);
  };

  return {
    searchTerm,
    results,
    loading,
    error,
    showResults,
    handleSearchChange,
    selectTicker,
    clearSearch,
  };
};
